import { createElement, useEffect, useRef, useState } from "react";
import { TaskRawStateEnum } from "../../annualWheel/AnnualWheel.types";
import { post, put } from "../../common/api/apiShared";
import { TrackingEvent, useTrackAIEvent } from "../../processingActivity/hooks/useTracking";
import {
    AddRelatedTaskScenario,
    AddTaskClickEnum,
    AnnualWheelActivityTaskNavigationViewModel,
    TaskEvaluationSaveModel,
    TaskSaveModel,
    TaskViewModel,
} from "./TaskPaneContent.types";
import { ITaskContent } from "./TaskPaneContent";
import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate";
import { getRelationsUrl } from "../../common/hooks/Relations.hooks.ts";
import { getTaskMasterDataUrl } from "./masterDataSection/MasterDataSection.hooks.ts";
import { downloadFile } from "../../common/api/downloadFile.ts";
import { getSettings } from "../../common/settingsProvider.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { createPdfUrl } from "../../common/pdfUrlHelper.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { Result, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation.ts";
import { Trans } from "react-i18next";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import { EvaluationStatus } from "../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import {
    DotLegalReactQueryOptions,
    useDotLegalQueryWithKey,
    useDotLegalQueryClient,
    useDotLegalMutation,
    useDotLegalQuery,
} from "@common/hooks/dotLegalReactQuery";
import { useNavigate } from "react-router";

export function getTaskQueryKey(taskId: string) {
    return "task" + taskId;
}

export function useTaskQuery(taskId: string, options?: DotLegalReactQueryOptions) {
    return useDotLegalQueryWithKey<TaskViewModel>(getTaskQueryKey(taskId), `/task/${taskId}`, options);
}

export function toggleEvaluationStatus(currentStatus: EvaluationStatus | undefined, selectedStatus: EvaluationStatus) {
    return currentStatus === selectedStatus ? undefined : selectedStatus;
}

export function putTaskEvaluation(taskId: string, saveModel: TaskEvaluationSaveModel) {
    return put<TaskEvaluationSaveModel>(`/task/${taskId}/evaluate`, saveModel);
}

interface TaskNavigation {
    openedTaskId: string;
    activeTaskId: string;
}

export function useTaskPaneNavigation(openedTaskId: string) {
    // The navigation is remembered together with the task the dialog was opened for, so a new taskId from the
    // opener (e.g. navigating to a freshly created evaluation task) wins over stale in-dialog navigation without
    // an effect that mirrors props into state.
    const [navigation, setNavigation] = useState<TaskNavigation>();
    const activeTaskId = navigation?.openedTaskId === openedTaskId ? navigation.activeTaskId : openedTaskId;

    const { data: task } = useTaskQuery(activeTaskId);

    const navigateToTask = (taskId: string) => {
        setNavigation({ openedTaskId, activeTaskId: taskId });
    };

    return {
        activeTaskId,
        navigateToTask,
        notPartOfCurrentPlan: task?.notPartOfCurrentPlan,
    };
}

export function useTaskPaneContentDataMapping(props: ITaskContent) {
    const snackBar = useDotLegalSnackbar();
    const queryClient = useDotLegalQueryClient();
    const trackEvent = useTrackAIEvent();
    const optimisticUpdate = useOptimisticUpdate();
    const evaluationRef = useRef<HTMLTextAreaElement>();
    const documentationRef = useRef<HTMLDivElement>(null);
    const evaluationSectionRef = useRef<HTMLDivElement>(null);

    const { getTaskPdfUrl } = useUrlProvider();
    const { language } = useUserContext();
    const { translateString } = useTranslation();
    const { getTaskManagementTaskDialogUrl } = useUrlProvider();
    const navigate = useNavigate();

    const queryKey = getTaskQueryKey(props.taskId);

    const [addTask, setAddTask] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSendReminderDialog, setShowSendReminderDialog] = useState(false);
    const [addDocument, setAddDocument] = useState<AddTaskClickEnum>(AddTaskClickEnum.None);
    const [showEvaluation, setShowEvaluation] = useState<boolean>(false);

    const { data, refetch } = useTaskQuery(props.taskId);
    const { data: openedTask } = useTaskQuery(props.openedTaskId);
    const { data: activityNavigation } = useDotLegalQuery<AnnualWheelActivityTaskNavigationViewModel>(`/task/${props.taskId}/annual-wheel-activity-navigation`);
    const isViewingOpenedTask = props.taskId === props.openedTaskId;

    // Only the task the host opened may move the year filter behind it. Stepping through sibling tasks on
    // the activity is a side trip that must leave the underlying page untouched.
    useEffect(() => {
        if (data?.deadline && isViewingOpenedTask) {
            props.setYearByTaskYear?.(new Date(data.deadline).getFullYear());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const saveCreateEvaluationTaskMutation = useDotLegalMutation(createEvaluationTaskUrl, {
        onSuccess: (response: any) => {
            navigate(getTaskManagementTaskDialogUrl(response.value().id));
        },
    });

    const createEvaluationTask = async () => {
        await saveCreateEvaluationTaskMutation.mutateAsync();
    };

    function createEvaluationTaskUrl() {
        return post<string>(`/task/${props.taskId}/evaluationTask`, null);
    }

    const taskUpdater = {
        onNameChange: async (name: string) => {
            let model = { ...data! };
            model.name = name;
            await onNameAndDescriptionChange(model);
        },
        onDescriptionChange: async (description: string) => {
            let model = { ...data! };
            model.description = description;
            await onNameAndDescriptionChange(model);

            queryClient.refetchQueries(getRelationsUrl(props.taskId));
        },
    };

    const evaluationUpdater = {
        onEvaluationStatusChange: async (evaluationStatus: EvaluationStatus) => {
            let model = { ...data! };
            model.evaluationStatus = toggleEvaluationStatus(model.evaluationStatus, evaluationStatus);
            await onEvaluationChange(model);
        },
        onEvaluationChange: async (evaluation: string) => {
            let model = { ...data! };
            model.evaluation = evaluation;
            await onEvaluationChange(model);
        },
    };

    async function updateTaskState() {
        const tempTaskModel = { ...data! };

        if (tempTaskModel.taskState === TaskRawStateEnum.Ready) {
            tempTaskModel.taskState = TaskRawStateEnum.InProgress;

            await optimisticUpdate.addToQueue(updateTaskStatus, tempTaskModel.taskState, queryKey);

            queryClient.refetchQueries(getTaskMasterDataUrl(props.taskId));
        }
    }

    async function onNameAndDescriptionChange(viewModel: TaskViewModel) {
        await optimisticUpdate.putOnQueueAndSetQueryData(viewModel, queryKey, updateNameAndDescription, mapToSaveModel(viewModel));
        trackEvent(TrackingEvent.AnnualWheelActivityTaskUpdated, { taskId: data?.id });
    }

    async function onEvaluationChange(viewModel: TaskViewModel) {
        await optimisticUpdate.putOnQueueAndSetQueryData(viewModel, queryKey, (saveModel) => putTaskEvaluation(props.taskId, saveModel), {
            evaluation: viewModel.evaluation,
            evaluationStatus: viewModel.evaluationStatus,
        });
        trackEvent(TrackingEvent.AnnualWheelActivityTaskUpdated, { taskId: data?.id });
    }

    async function onTaskDocumentChange(hasDocuments: boolean) {
        const tempTaskModel = { ...data! };
        tempTaskModel.hasDocument = hasDocuments;
        queryClient.setQueryData(queryKey, tempTaskModel);
    }

    async function onDownloadClick() {
        snackBar.show(translateString("downloading"), "info", true);
        const fileName = `${data?.name}`;

        let queryUrl = getTaskPdfUrl(props.taskId);
        let url = getSettings().apiBaseUrl + `/task/${props.taskId}/download?url=${createPdfUrl(queryUrl, language)}`;

        await downloadFile(url, `${fileName}.zip`).then((x) => {
            snackBar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName: fileName } }));
        });
    }

    const onShowEvaluationClick = () => {
        setShowEvaluation(true);
        evaluationRef?.current?.focus();
        if (evaluationRef?.current) {
            const length = evaluationRef.current.value.length;
            evaluationRef.current.setSelectionRange(length, length);
        }
        // Like documentation, the section mounts on the render this click triggers and may be out of sight.
        requestAnimationFrame(() => evaluationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
    };

    const onHideEvaluationClick = async () => {
        let model = { ...data! };
        model.evaluationStatus = undefined;
        model.evaluation = undefined;
        await onEvaluationChange(model);
        setShowEvaluation(false);
    };

    const shownAddDocument =
        addDocument === AddTaskClickEnum.None && (data?.documentationRequired || data?.hasDocument) ? AddTaskClickEnum.ShowSection : addDocument;

    function onAddDocumentationClick() {
        switch (shownAddDocument) {
            case AddTaskClickEnum.None:
                setAddDocument(AddTaskClickEnum.ShowSection);
                break;
            case AddTaskClickEnum.ShowSection:
                setAddDocument(AddTaskClickEnum.ShowFileExplorer);
                break;
        }

        // In the collapsed panel the section sits below Details and Relations, out of sight, so the click
        // looked like it did nothing. It only mounts on the render this click triggers, hence the frame's wait.
        requestAnimationFrame(() => documentationRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
    }

    return {
        task: data,
        activityNavigation: activityNavigation && activityNavigation.total > 1 ? activityNavigation : undefined,
        backToOpenedTask: isViewingOpenedTask ? undefined : { taskId: props.openedTaskId, deadline: openedTask?.deadline },
        refetchTask: refetch,
        updateTaskState,
        onTaskDocumentChange,
        addTask,
        setAddTask,
        addDocument: shownAddDocument,
        setAddDocument,
        onAddDocumentationClick,
        documentationRef,
        evaluationSectionRef,
        showDeleteDialog,
        setShowDeleteDialog,
        showSendReminderDialog,
        setShowSendReminderDialog,
        taskUpdater,
        onDownloadClick,
        showEvaluation:
            data && data.taskState === TaskRawStateEnum.Completed && (showEvaluation || !!data.evaluationStatus || !isNullOrWhitespace(data.evaluation)),
        onShowEvaluationClick,
        // An evaluation task follows up on another task's evaluation, so it is not evaluated itself
        showEvaluateButton: data?.taskState === TaskRawStateEnum.Completed && !data.isEvaluationTask,
        evaluationUpdater,
        evaluationRef,
        onHideEvaluationClick,
        createEvaluationTask,
        isCreatingEvaluationTask: saveCreateEvaluationTaskMutation.isLoading,
    };

    function mapToSaveModel(viewModel: TaskViewModel): TaskSaveModel {
        return {
            title: viewModel.name,
            description: viewModel.description,
        };
    }

    async function updateNameAndDescription(saveModel: TaskSaveModel) {
        return await put<TaskSaveModel>("/Task/" + props.taskId, saveModel);
    }

    async function updateTaskStatus(state: TaskRawStateEnum) {
        return await put<TaskSaveModel>("/Task/" + props.taskId + "/taskStatus", state);
    }
}
